import React, { useMemo } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { exportToExcel, ExcelData } from "../../../utils/excelExport";
import styles from "./pieChart.module.scss";

interface PieChartProps {
  title?: string;
  data?: any[];
  nameKey?: string;
  valueKey?: string;
  colorKey?: string;
  showPercentage?: boolean;
  outerRadius?: number;
  onDownload?: () => void;
  excelFileName?: string;
  hideLabels?: boolean;
}

const CustomPieChart: React.FC<PieChartProps> = ({
  title = "Pie Chart",
  data = [],
  nameKey = "name",
  valueKey = "value",
  colorKey = "color",
  showPercentage = true,
  outerRadius = 120,
  onDownload,
  excelFileName = "Pie_Chart_Data",
  hideLabels = false,
}) => {
  const defaultColors = [
    "#00d2aa",
    "#5b6b9e",
    "#7b8fc4",
    "#a0aed9",
    "#c5cde8",
    "#ff6b6b",
    "#ffa94d",
    "#74c0fc",
  ];

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }

    const excelData: ExcelData = {
      sheetName: title,
      fileName: title.replace(/\s+/g, "_"),
      columns: [
        { header: "Category", key: "name", width: 50 },
        { header: showPercentage ? "Percentage (%)" : "Value", key: "value", width: 20 },
      ],
      data: chartData.map((item) => ({
        name: item.name,
        value: showPercentage && total > 0
          ? parseFloat(((item.value / total) * 100).toFixed(2))
          : item.value,
      })),
    };

    exportToExcel(excelData);
  };

  // Transform and normalize data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => ({
      name: item[nameKey] || item.group || item.name || `Item ${index + 1}`,
      value: Number(item[valueKey] || item.value || 0),
      color:
        item[colorKey] ||
        item.color ||
        defaultColors[index % defaultColors.length],
    }));
  }, [data, nameKey, valueKey, colorKey, defaultColors]);

  // Calculate total for percentage
  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0);
  }, [chartData]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const percentage =
        total > 0 ? ((payload[0].value / total) * 100).toFixed(1) : 0;
      return (
        <Box className={styles.customTooltip}>
          <Typography className={styles.tooltipLabel}>
            {payload[0].name}
          </Typography>
          <Typography className={styles.tooltipValue}>
            {showPercentage ? `${percentage}%` : payload[0].value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const RADIAN = Math.PI / 180;

  const trimToWord = (text: string, maxChars: number) => {
    if (text.length <= maxChars) {
      return { line: text, rest: "" };
    }
    const slice = text.slice(0, maxChars);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0) {
      return {
        line: slice.slice(0, lastSpace).trim(),
        rest: text.slice(lastSpace + 1).trim(),
      };
    }
    return { line: slice.trim(), rest: text.slice(maxChars).trim() };
  };

  const renderCustomLabel = (props: any) => {
    const {
      cx,
      cy,
      midAngle,
      outerRadius: radiusFromProps,
      value,
      name,
    } = props;

    const radius = (radiusFromProps || outerRadius) + 24;
    // Slight offset to reduce label overlap in dense charts.
    const labelOffsetX = -10; // left
    const labelOffsetY = 12; // down

    const x = cx + radius * Math.cos(-midAngle * RADIAN) + labelOffsetX;
    const y = cy + radius * Math.sin(-midAngle * RADIAN) + labelOffsetY;

    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

    // Hide label if percentage is below 3%
    if (Number(percentage) < 3) {
      return null;
    }

    const nameText = String(name ?? "").trim();

    const firstSplit = trimToWord(nameText, 20);
    const secondSplit = trimToWord(firstSplit.rest, 16);
    const needsEllipsis = secondSplit.rest.length > 0;

    const firstLine = firstSplit.line;
    const secondLine = firstSplit.rest
      ? `${secondSplit.line}${needsEllipsis ? "..." : ""}`
      : "";

    const textAnchor = x > cx ? "start" : "end";

    return (
      <text x={x} y={y} textAnchor={textAnchor} dominantBaseline="auto">
        {!firstSplit.rest ? (
          <tspan x={x} dy="0em">
            {nameText}
          </tspan>
        ) : (
          <>
            <tspan x={x} dy="-0.3em">
              {firstLine}
            </tspan>
            <tspan x={x} dy="1.2em">
              {secondLine}
            </tspan>
          </>
        )}
      </text>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.chartWrapper}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
        <IconButton
          onClick={handleDownload}
          size="small"
          sx={{
            color: "#5b6b9e",
            "&:hover": {
              backgroundColor: "rgba(91, 107, 158, 0.1)",
            },
          }}
        >
          <FileDownloadOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box className={styles.chartWrapper}>
        <Box className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={hideLabels ? false : renderCustomLabel}
                outerRadius={outerRadius}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomPieChart;
